/// <amd-module name="Artifacts/Feed/components/FeedHubState" />
import { ProblemPackageItem } from "Artifacts/Feed/types/ProblemPackageItem";
import { ComponentState } from "Artifacts/Root/Constants";
import { isReady } from "Artifacts/Root/helpers/ComponentStateHelpers";
import { IFeed } from "Artifacts/Root/types";
import { PackageListDataItem } from "Artifacts/Root/types/AzureArtifacts";
import * as React from "react";

export interface IFeedHubStateProps {
  feed?: IFeed;
  packageListComponentState: ComponentState;
  problemPackageListComponentState: ComponentState;
  problemPackageData?: ProblemPackageItem[];
  packageData?: PackageListDataItem[];
  isFilterActive: boolean;
  showCFSUI: boolean;
}

const WhenFeedWithPackagesOrFiltered: React.FC = justChildrenComponent();
const WhenEmptyFeed: React.FC = justChildrenComponent();
const WhenFeedIsDeleted: React.FC = justChildrenComponent();
const WhenProblemPackagesExist: React.FC = justChildrenComponent();

/**
 * FeedHubState encapsulate different state conditions and provide JSX based switch-like code structure.
 * Example:
 * <FeedHubState>
 *   <WhenFeedWithPackages>{render package table}</WhenWhenFeedWithPackages>
 *   <WhenEmptyFeed>{render no data component}</WhenEmptyFeed>
 * </FeedHubState>
 */
const FeedHubState: React.FC<IFeedHubStateProps> = ({
  children,
  feed,
  packageListComponentState,
  problemPackageListComponentState,
  packageData,
  problemPackageData,
  isFilterActive,
  showCFSUI,
}) => {
  if (
    isReady(packageListComponentState) &&
    isReady(problemPackageListComponentState) &&
    problemPackageData &&
    problemPackageData.length > 0 &&
    showCFSUI
  ) {
    return getChildrenOfType(children, WhenProblemPackagesExist);
  }
  if (
    (isReady(packageListComponentState) && packageData && packageData.length > 0) ||
    (isReady(packageListComponentState) && isFilterActive && !feed?.isDeleted)
  ) {
    return getChildrenOfType(children, WhenFeedWithPackagesOrFiltered);
  }
  if (isReady(packageListComponentState) && !feed?.isDeleted) {
    return getChildrenOfType(children, WhenEmptyFeed);
  }
  if (isReady(packageListComponentState) && feed?.isDeleted) {
    return getChildrenOfType(children, WhenFeedIsDeleted);
  }
  return null;
};

/**
 * getChildrenOfType filters React children and return last child of requested type.
 */
function getChildrenOfType(children: any, type: string | React.JSXElementConstructor<any>) {
  let returnChild = null;
  React.Children.forEach(
    children,
    (child: React.ReactElement<any, string | React.JSXElementConstructor<any>>, i) => {
      if (child.type === type) {
        returnChild = child;
      }
    },
  );
  return returnChild;
}

function justChildrenComponent(): React.FC {
  return ({ children }) => <>{children}</>;
}

export {
  FeedHubState,
  WhenFeedWithPackagesOrFiltered,
  WhenEmptyFeed,
  WhenFeedIsDeleted,
  WhenProblemPackagesExist,
};
